import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export function createCamera(canvas) {
	const camera = new THREE.PerspectiveCamera(45, canvas.clientWidth / canvas.clientHeight, 0.1, 10000);
	camera.position.set(0, 0, 10);
	camera.up.set(0, 0, 1);

	const controls = new OrbitControls(camera, canvas);
	controls.enableDamping = true;
	controls.dampingFactor = 0.1;
	controls.screenSpacePanning = true;
	controls.minDistance = 0.1;
	controls.maxDistance = 10000;

	controls.isDragging = false;
	const DRAG_THRESHOLD_PX = 4;
	let _pointerDown = false;
	let _pointerId = null;
	let _startX = 0;
	let _startY = 0;

	function _onPointerDown(ev) {
		if (ev.pointerType === 'mouse' && ev.button !== 0) return;
		_pointerDown = true;
		_pointerId = ev.pointerId || null;
		_startX = ev.clientX;
		_startY = ev.clientY;
		controls.isDragging = false;
		try { ev.target && ev.target.setPointerCapture && ev.target.setPointerCapture(ev.pointerId); } catch (e) { }
	}

	function _onPointerMove(ev) {
		if (!_pointerDown) return;
		if (_pointerId && ev.pointerId !== _pointerId) return;
		const dx = ev.clientX - _startX;
		const dy = ev.clientY - _startY;
		if (!controls.isDragging && (dx * dx + dy * dy) >= (DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX)) {
			controls.isDragging = true;
		}
	}

	function _onPointerUp(ev) {
		if (_pointerId && ev.pointerId !== _pointerId) return;
		_pointerDown = false;
		_pointerId = null;
		if (controls.isDragging) {
			setTimeout(() => { controls.isDragging = false; }, 50);
		} else { controls.isDragging = false; }
		try { ev.target && ev.target.releasePointerCapture && ev.target.releasePointerCapture(ev.pointerId); } catch (e) { }
	}

	if (canvas && canvas.addEventListener) {
		canvas.addEventListener('pointerdown', _onPointerDown);
		canvas.addEventListener('pointermove', _onPointerMove);
		canvas.addEventListener('pointerup', _onPointerUp);
		canvas.addEventListener('pointercancel', _onPointerUp);
	} else {
		controls.addEventListener('start', () => { controls.isDragging = true; });
		controls.addEventListener('end', () => { setTimeout(() => { controls.isDragging = false; }, 50); });
	}

	function setBoundsForBox(box3, { offset = 1.1 } = {}) {
		if (!box3 || (typeof box3.isEmpty === 'function' && box3.isEmpty())) return;
		const center = new THREE.Vector3();
		const size = new THREE.Vector3();
		box3.getCenter(center);
		box3.getSize(size);
		const maxDim = Math.max(size.x, size.y, size.z);
		const radius = maxDim * 0.5;

		const fov = camera.fov * (Math.PI / 180);
		const aspect = camera.aspect || (canvas.clientWidth / canvas.clientHeight) || 1;

		const d_v = radius / Math.tan(fov / 2);
		const d_h = radius / (Math.tan(fov / 2) * aspect);
		const required = Math.max(d_v, d_h) * offset;

		camera.near = Math.max(0.001, required * 0.001);
		camera.far = Math.max(1000, required * 10);
		controls.maxDistance = camera.far;
		controls.maxTargetRadius = radius * 10;
	}

	function fitToBox(box3, { offset = 1.1, animate = true, duration = 400 } = {}) {
		if (!box3 || (typeof box3.isEmpty === 'function' && box3.isEmpty())) return;
		const center = new THREE.Vector3();
		const size = new THREE.Vector3();
		box3.getCenter(center);
		box3.getSize(size);
		const maxDim = Math.max(size.x, size.y, size.z);
		const radius = maxDim * 0.5;

		const fov = camera.fov * (Math.PI / 180);
		const aspect = camera.aspect || (canvas.clientWidth / canvas.clientHeight) || 1;

		const d_v = radius / Math.tan(fov / 2);
		const d_h = radius / (Math.tan(fov / 2) * aspect);
		let required = Math.max(d_v, d_h) * offset;

		const forward = new THREE.Vector3();
		camera.getWorldDirection(forward);
		const right = new THREE.Vector3().copy(forward).cross(camera.up).normalize();
		const up = new THREE.Vector3().copy(right).cross(forward).normalize();

		const basePos = new THREE.Vector3().copy(center).sub(forward.clone().multiplyScalar(required));
		const v = new THREE.Vector3().copy(center).sub(basePos);
		const x = v.dot(right);
		const y = v.dot(up);

		const newPos = basePos.clone().add(right.multiplyScalar(x)).add(up.multiplyScalar(y));

		if (!animate) {
			camera.position.copy(newPos);
			camera.updateProjectionMatrix();
			controls.target.copy(center);
			controls.update();
			return;
		}

		const startPos = camera.position.clone();
		const startTarget = controls.target.clone();
		const endPos = newPos.clone();
		const endTarget = center.clone();
		const t0 = performance.now();
		function step(now) {
			const t = Math.min(1, (now - t0) / duration);
			const ease = t * (2 - t);
			camera.position.lerpVectors(startPos, endPos, ease);
			controls.target.lerpVectors(startTarget, endTarget, ease);
			controls.update();
			if (t < 1) requestAnimationFrame(step);
			else { camera.position.copy(endPos); controls.target.copy(endTarget); controls.update(); camera.updateProjectionMatrix(); }
		}
		requestAnimationFrame(step);
	}

	function dispose() {
		try {
			if (canvas && canvas.removeEventListener) {
				canvas.removeEventListener('pointerdown', _onPointerDown);
				canvas.removeEventListener('pointermove', _onPointerMove);
				canvas.removeEventListener('pointerup', _onPointerUp);
				canvas.removeEventListener('pointercancel', _onPointerUp);
			}
		} catch (e) { }
	}

	return { camera, controls, fitToBox, setBoundsForBox, dispose };
}

export default createCamera;
